import { UserApplication } from '@/application/user/user';
import type { TUserCountsOrFetchResult, TUserSocialGraphStatusResult } from '@/application/user/user.types';
import type { TReadProfileParams } from '@/controllers/profile/profile.types';
import { captureViewerSession } from '@/controllers/tag/tag-cache.utils';
import type {
  TFetchUserDetailsParams,
  TFetchUserParams,
  TFollowParams,
  TPubkyListParams,
} from '@/controllers/user/user.type';
import { HttpMethod } from '@/libs/http/http.types';
import { stripPubkyPrefix } from '@/libs/utils/utils';
import type { Pubky } from '@/models/models.types';
import type { UserCountsModel } from '@/models/user/counts/userCounts';
import type { UserRelationshipsModelSchema } from '@/models/user/relationships/userRelationships.schema';
import { FollowNormalizer } from '@/pipes/follow/follow.normalizer';
import type {
  NexusTag,
  NexusTaggers,
  NexusUserCounts,
  NexusUserDetails,
  NexusUserRelationship,
} from '@/services/nexus/nexus.types';
import type { TUserTaggersParams } from '@/services/nexus/user/user.types';
import { useAuthStore } from '@/stores/auth/auth.store';

export class UserController {
  private constructor() {} // Prevent instantiation

  /**
   * Get user details from local database
   * This is a read-only operation that queries the local cache
   */
  static async getDetails(params: TReadProfileParams): Promise<NexusUserDetails | null> {
    return await UserApplication.getDetails(params);
  }

  /**
   * Get multiple user details from local database (bulk operation)
   * This is a read-only operation that queries the local cache
   */
  static async getManyDetails(params: TPubkyListParams): Promise<Map<Pubky, NexusUserDetails>> {
    return await UserApplication.getManyDetails(params);
  }

  /**
   * Get a user's social graph badge tier from local database.
   * This is a read-only operation that queries the local cache.
   * Returns null while the tier is unknown (no full user view cached yet).
   */
  static async getSocialGraphStatus(params: TReadProfileParams): Promise<TUserSocialGraphStatusResult | null> {
    return await UserApplication.getSocialGraphStatus(params);
  }

  /**
   * Get user counts from local database.
   * This is a read-only operation that queries the local cache.
   */
  static async getCounts(params: TReadProfileParams): Promise<UserCountsModel | null> {
    return await UserApplication.getCounts(params);
  }

  /**
   * Get user counts from local database, fetching from Nexus API if not found.
   */
  static async getOrFetchCounts(params: TReadProfileParams): Promise<TUserCountsOrFetchResult | null> {
    return await UserApplication.getOrFetchCounts({ ...params, isCurrent: captureViewerSession() });
  }

  /**
   * Fetch user counts from Nexus and persist locally (network-only, no local read).
   * Use instead of `getOrFetchCounts` when the caller already knows counts are not cached.
   */
  static async fetchCounts(params: TReadProfileParams): Promise<NexusUserCounts | null> {
    return await UserApplication.fetchCounts({ ...params, isCurrent: captureViewerSession() });
  }

  /**
   * Get multiple user counts from local database (bulk operation).
   * This is a read-only operation that queries the local cache.
   */
  static async getManyCounts(params: TPubkyListParams): Promise<Map<Pubky, NexusUserCounts>> {
    return await UserApplication.getManyCounts(params);
  }

  /**
   * Fetch taggers for a user from the Nexus API
   * @param params - The parameters for fetching taggers
   * @returns The taggers for the user
   */
  static async fetchTaggers(params: TUserTaggersParams): Promise<NexusTaggers> {
    return await UserApplication.fetchTaggers(params);
  }

  /**
   * Get user relationships from local database
   * This is a read-only operation that queries the local cache
   */
  static async getRelationships(params: TReadProfileParams): Promise<NexusUserRelationship | null> {
    return await UserApplication.getRelationships(params);
  }

  /**
   * Get multiple user relationships from local database (bulk operation)
   * This is a read-only operation that queries the local cache
   */
  static async getManyRelationships(params: TPubkyListParams): Promise<Map<Pubky, UserRelationshipsModelSchema>> {
    return await UserApplication.getManyRelationships(params);
  }

  /**
   * Scopes a full user fetch to a viewer. Defaults to the signed-in user so the persisted
   * relationship row reflects them; `currentUserPubky` is read directly (not via
   * `selectCurrentUserPubky`, which throws) because guests can open profiles too.
   */
  private static withViewer({ userId, viewerId }: TFetchUserParams): TFetchUserParams {
    return { userId, viewerId: viewerId ?? useAuthStore.getState().currentUserPubky ?? undefined };
  }

  /**
   * Get full user entity from local database or fetch from Nexus batch API.
   * Persists details, counts, relationships, tags, TTL, and moderation.
   * Preferred over `getOrFetchDetails` when the caller needs the full entity cached.
   * The fetch is scoped to `viewerId`, defaulting to the signed-in user so the relationship
   * row is viewer-relative.
   */
  static async getOrFetch(params: TFetchUserParams): Promise<NexusUserDetails | null> {
    return await UserApplication.getOrFetch({ ...this.withViewer(params), isCurrent: captureViewerSession() });
  }

  /**
   * Fetch full user entity from Nexus batch API and persist locally (network-only, no local read).
   * Use instead of `getOrFetch` when the caller already knows the user is not cached.
   * The fetch is scoped to `viewerId`, defaulting to the signed-in user, so the persisted
   * relationship row never reads as "not following" for a user the viewer follows.
   */
  static async fetch(params: TFetchUserParams): Promise<NexusUserDetails | null> {
    return await UserApplication.fetch({ ...this.withViewer(params), isCurrent: captureViewerSession() });
  }

  /**
   * Get user details from local database or fetch from Nexus API
   * This is a read-only operation that queries the local cache
   */
  static async getOrFetchDetails(param: TReadProfileParams): Promise<NexusUserDetails | null> {
    return await UserApplication.getOrFetchDetails({ ...param, isCurrent: captureViewerSession() });
  }

  /**
   * Fetch user details from Nexus and persist locally (network-only, no local read).
   * Use instead of `getOrFetchDetails` when the caller already knows the user is not cached.
   */
  static async fetchDetails(param: TFetchUserDetailsParams): Promise<NexusUserDetails | null> {
    return await UserApplication.fetchDetails({ ...param, isCurrent: captureViewerSession() });
  }

  /**
   * Get multiple user tags with local-first strategy (bulk operation)
   * Reads from cache first, fetches from API only for missing users
   */
  static async getManyTagsOrFetch(params: TPubkyListParams & { viewerId?: Pubky }): Promise<Map<Pubky, NexusTag[]>> {
    // Same viewer default as `withViewer`: missing windows are fetched and stored for the signed-in user.
    const viewerId = params.viewerId ?? useAuthStore.getState().currentUserPubky ?? undefined;
    return await UserApplication.getManyTagsOrFetch({ ...params, viewerId, isCurrent: captureViewerSession() });
  }

  /**
   * Commit a follow action to indexeddb and the homeserver
   * @param eventType - The event type (PUT or DELETE)
   * @param follower - The follower user ID
   * @param followee - The followee user ID
   */
  static async commitFollow(eventType: HttpMethod, { follower, followee }: TFollowParams) {
    // Normalize pubky IDs to ensure consistent format for storage and comparison
    // This strips any prefix (pubky or pk:) to get the raw 52-character key
    const normalizedFollowee = stripPubkyPrefix(followee) as Pubky;
    const { meta, follow } = FollowNormalizer.to({ follower, followee: normalizedFollowee });

    await UserApplication.commitFollow({
      eventType,
      followUrl: meta.url,
      followJson: follow.toJson(),
      follower,
      followee: normalizedFollowee,
    });
  }
}
